import { memo, useCallback, useMemo } from 'react';
import {
  cn,
  LottieController,
  LottiePosition,
  LottieAnimation,
  LottieAnimationProps,
} from '@bridebook/aura';

export interface LottieRelativeAnimationProps extends LottieAnimationProps {
  /**
   * Controller object created by useLottieController().
   * Used to trigger animation playback.
   * If not passed, animation will autoplay.
   */
  controller?: LottieController;

  /**
   * Whether the animation should loop or play just once.
   * @default false
   */
  loop?: boolean;

  /**
   * Position of the animation relative to the parent container.
   * Outside positions place animation beyond borders, inside positions within borders.
   * @default LottiePosition.Centered
   */
  position?: LottiePosition;

  /**
   * Additional horizontal offset (in pixels) from the calculated position.
   * @default 0
   */
  offsetX?: number;

  /**
   * Additional vertical offset (in pixels) from the calculated position.
   * @default 0
   */
  offsetY?: number;
}

/**
 * Get Tailwind classes for positioning based on LottiePosition
 * Outside positions place animation beyond element borders
 * Inside positions place animation within element borders
 */
const getPositionClasses = (position: LottiePosition): string => {
  switch (position) {
    // Outside positions (beyond borders)
    case LottiePosition.Top:
      return 'bottom-full left-1/2 -translate-x-1/2';
    case LottiePosition.Bottom:
      return 'top-full left-1/2 -translate-x-1/2';
    case LottiePosition.Left:
      return 'right-full top-1/2 -translate-y-1/2';
    case LottiePosition.Right:
      return 'left-full top-1/2 -translate-y-1/2';
    case LottiePosition.TopLeft:
      return 'bottom-full right-full';
    case LottiePosition.TopRight:
      return 'bottom-full left-full';
    case LottiePosition.BottomLeft:
      return 'top-full right-full';
    case LottiePosition.BottomRight:
      return 'top-full left-full';
    // Inside positions (within borders)
    case LottiePosition.InsideTop:
      return 'top-0 left-1/2 -translate-x-1/2';
    case LottiePosition.InsideBottom:
      return 'bottom-0 left-1/2 -translate-x-1/2';
    case LottiePosition.InsideLeft:
      return 'left-0 top-1/2 -translate-y-1/2';
    case LottiePosition.InsideRight:
      return 'right-0 top-1/2 -translate-y-1/2';
    case LottiePosition.InsideTopLeft:
      return 'top-0 left-0';
    case LottiePosition.InsideTopRight:
      return 'top-0 right-0';
    case LottiePosition.InsideBottomLeft:
      return 'bottom-0 left-0';
    case LottiePosition.InsideBottomRight:
      return 'bottom-0 right-0';
    // Centered
    case LottiePosition.Centered:
    default:
      return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
  }
};

/**
 * Renders Lottie animation with absolute positioning relative to parent container.
 * Parent must have position: relative or position: absolute.
 */
const LottieRelativeAnimationComp = ({
  animation,
  controller,
  position = LottiePosition.Centered,
  width,
  height,
  offsetX = 0,
  offsetY = 0,
  loop = false,
  className = '',
  onAnimationFinished,
}: LottieRelativeAnimationProps) => {
  const { stop: stopPlaying, isPlaying } = controller || {
    isPlaying: true, // Autoplay if controller is not provided
  };

  /**
   * Handle animation completion - stop controller and call user callback
   */
  const handleAnimationFinished = useCallback(() => {
    stopPlaying?.();
    onAnimationFinished?.();
  }, [stopPlaying, onAnimationFinished]);

  const positionClasses = useMemo(() => getPositionClasses(position), [position]);

  if (!isPlaying) return null;

  // Use inline styles for offsets to avoid conflicts with Tailwind translate classes
  const offsetStyle =
    offsetX !== 0 || offsetY !== 0
      ? {
          transform: `translate(${offsetX}px, ${offsetY}px)`,
        }
      : undefined;

  return (
    <div
      className={cn('pointer-events-none absolute z-10', positionClasses, className)}
      style={offsetStyle}>
      <LottieAnimation
        animation={animation}
        width={width}
        height={height}
        loop={loop}
        onAnimationFinished={handleAnimationFinished}
      />
    </div>
  );
};

export const LottieRelativeAnimation = memo(LottieRelativeAnimationComp);
